import { useState, useEffect, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import {
    RefreshCw, Loader2, CheckCircle2, XCircle, AlertTriangle, Server, Send,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import {
    EMAIL_SENDING_POOL_URL, EMAIL_ACCOUNTS_URL, EMAIL_ACCOUNT_VERIFY_SMTP_URL,
} from "../../constants/api";

const Stat = ({ label, value, sub, tone }) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
        <div className={cn("mt-1 text-xl font-semibold tabular-nums", tone || "text-gray-800 dark:text-gray-100")}>{value}</div>
        {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
);

export default function SendingPool() {
    const { fetch, update, create } = useApiMethods();
    const [pool, setPool] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [draft, setDraft] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, a] = await Promise.all([
                fetch(EMAIL_SENDING_POOL_URL),
                fetch(EMAIL_ACCOUNTS_URL),
            ]);
            if (p?.status === 'success') setPool(p.data);
            if (a?.status === 'success') setAccounts(a.data || []);
        } catch {
            ToastMsg("error", "Could not load the sending pool");
        }
        setLoading(false);
    }, [fetch]);

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const usageFor = (id) => pool?.accounts?.find(r => r.id === id);

    const save = async (acc, patch) => {
        setBusyId(acc.id);
        try {
            const res = await update(EMAIL_SENDING_POOL_URL, { id: acc.id, ...patch });
            if (res?.status === 'success') { ToastMsg("success", "Saved"); load(); }
            else ToastMsg("error", res?.message || "Could not save");
        } catch {
            ToastMsg("error", "Could not save");
        }
        setBusyId(null);
    };

    const verify = async (acc) => {
        setBusyId(acc.id);
        try {
            const res = await create(EMAIL_ACCOUNT_VERIFY_SMTP_URL(acc.id), {});
            if (res?.status === 'success') { ToastMsg("success", "SMTP verified"); load(); }
            else ToastMsg("error", res?.message || "SMTP check failed");
        } catch {
            ToastMsg("error", "SMTP check failed");
        }
        setBusyId(null);
    };

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Sending Pool</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Campaigns draw from every enabled mailbox, respecting each one's daily limit.
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={load} disabled={loading}
                            className="flex items-center gap-1 text-xs">
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                    </Button>
                </div>

                {pool && (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <Stat label="Mailboxes" value={pool.account_count} sub="enabled &amp; verified" />
                            <Stat label="Daily capacity" value={pool.daily_capacity.toLocaleString()} sub="emails per day" />
                            <Stat label="Sent today" value={pool.used_today.toLocaleString()} />
                            <Stat label="Remaining today" value={pool.remaining_today.toLocaleString()}
                                  tone={pool.remaining_today === 0 ? "text-amber-600 dark:text-amber-500" : undefined} />
                        </div>

                        {pool.account_count === 0 && (
                            <div className="mb-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    No mailbox is enabled for sending, so campaigns cannot start.
                                    Set an SMTP host below, verify it, then switch it on.
                                </span>
                            </div>
                        )}

                        {pool.mixed_domains?.length > 0 && (
                            <div className="mb-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    The pool spans several domains ({pool.mixed_domains.join(', ')}). Each message
                                    is sent by the mailbox that carries it, so recipients will see the From address
                                    change between messages, and every domain needs its own SPF and DKIM records.
                                </span>
                            </div>
                        )}
                    </>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                <th className="pb-2 font-medium">Mailbox</th>
                                <th className="pb-2 font-medium">SMTP</th>
                                <th className="pb-2 font-medium">Sending</th>
                                <th className="pb-2 font-medium">Daily limit</th>
                                <th className="pb-2 font-medium">Used today</th>
                                <th className="pb-2 font-medium">Priority</th>
                                <th className="pb-2 font-medium">In pool</th>
                                <th className="pb-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.map(a => {
                                const u = usageFor(a.id);
                                const d = draft[a.id] || {};
                                const verified = Boolean(a.smtp_verified_at);
                                return (
                                    <tr key={a.id} className="border-b border-gray-100 dark:border-gray-800">
                                        <td className="py-3">
                                            <div className="font-medium text-gray-800 dark:text-gray-100">{a.email_address}</div>
                                            <div className="text-[11px] text-gray-400">{a.provider_label || a.provider}</div>
                                        </td>
                                        <td className="py-3 text-xs text-gray-500 dark:text-gray-400">
                                            {a.smtp_host
                                                ? <>{a.smtp_host}<span className="text-gray-400">:{a.smtp_port || 587}</span></>
                                                : <span className="text-amber-600 dark:text-amber-500">not set</span>}
                                        </td>
                                        <td className="py-3">
                                            {verified ? (
                                                <span className="inline-flex items-center gap-1 text-xs text-teal-600 dark:text-teal-400">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> verified
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs text-gray-400"
                                                      title={a.smtp_last_error || 'Never checked'}>
                                                    <XCircle className="h-3.5 w-3.5" /> unverified
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <Input
                                                type="number" min={0} max={5000}
                                                className="h-8 w-24"
                                                value={d.daily_send_limit ?? a.daily_send_limit ?? 400}
                                                onChange={e => setDraft(p => ({ ...p, [a.id]: { ...d, daily_send_limit: e.target.value } }))}
                                                onBlur={e => {
                                                    const v = Number(e.target.value);
                                                    if (v !== a.daily_send_limit) save(a, { daily_send_limit: v });
                                                }}
                                            />
                                        </td>
                                        <td className="py-3 tabular-nums text-gray-600 dark:text-gray-400">
                                            {u ? `${u.sent_today} / ${u.daily_send_limit}` : '—'}
                                            {u?.exhausted && (
                                                <span className="ml-1 text-[10px] font-medium uppercase text-amber-600 dark:text-amber-500">full</span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <Input
                                                type="number" className="h-8 w-20"
                                                value={d.send_priority ?? a.send_priority ?? 100}
                                                onChange={e => setDraft(p => ({ ...p, [a.id]: { ...d, send_priority: e.target.value } }))}
                                                onBlur={e => {
                                                    const v = Number(e.target.value);
                                                    if (v !== a.send_priority) save(a, { send_priority: v });
                                                }}
                                            />
                                        </td>
                                        <td className="py-3">
                                            <label className="inline-flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(a.can_send_campaigns)}
                                                    disabled={busyId === a.id}
                                                    onChange={e => save(a, { can_send_campaigns: e.target.checked })}
                                                />
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {a.can_send_campaigns ? 'enabled' : 'off'}
                                                </span>
                                            </label>
                                        </td>
                                        <td className="py-3">
                                            <Button variant="outline" size="sm" disabled={busyId === a.id || !a.smtp_host}
                                                    onClick={() => verify(a)}
                                                    className="flex items-center gap-1 text-xs">
                                                {busyId === a.id
                                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                                    : <Server className="h-3 w-3" />}
                                                Check SMTP
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {accounts.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="py-8 text-center text-sm text-gray-400">
                                        No mailboxes connected. Add one under Settings → Email Integration.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <p className="mt-4 flex gap-1.5 text-[11px] text-gray-400">
                    <Send className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                    Lower priority numbers drain first. Google Workspace and Microsoft 365 both cut off
                    somewhere around 500–2000 external recipients a day; keeping each limit under the real
                    cap avoids a temporary block. A campaign that exhausts the pool pauses and resumes the
                    next morning rather than failing.
                </p>
            </ContainerCard>
        </div>
    );
}
